import { useQuery } from '@tanstack/react-query';

import { ApiError } from '~/api/errors';
import { queryKeys } from '~/api/queryKeys';

import { getConnectionCapabilities } from '../services/meta-delivery.service';
import type { ConnectionCapabilities } from '../types/meta-delivery.types';

/**
 * Connector capability matrix for a connection — "which resources/actions does
 * this connector support?", used to hide UI it cannot do. A 404 means the
 * connector exposes no matrix and resolves to `null` (render nothing, not an
 * error); nothing is retried because the answer doesn't change between clicks.
 */
export function useConnectionCapabilities(connectionId: string | undefined, enabled = true) {
  return useQuery<ConnectionCapabilities | null>({
    queryKey: connectionId
      ? queryKeys.connections.capabilities(connectionId)
      : queryKeys.disabledDetail.connection(),
    queryFn: async () => {
      if (!connectionId) throw new Error('useConnectionCapabilities: id required when enabled');
      try {
        return await getConnectionCapabilities(connectionId);
      } catch (err) {
        if (err instanceof ApiError && err.statusCode === 404) return null;
        throw err;
      }
    },
    enabled: enabled && !!connectionId,
    staleTime: 5 * 60_000,
    retry: false,
  });
}
